import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { getInitials } from '@heidi/utils';
import { useIntl } from 'react-intl';

export interface ClinicianSelectorMember {
  kinde_user_id: string;
  name: string;
}

export interface ClinicianSelectorCommandProps {
  clinicians: ClinicianSelectorMember[];
  /** The current user's id — pins their row above the Team group when present. */
  currentUserId?: string;
  isLoading: boolean;
  /** Called with the picked clinician's member id. */
  onSelect: (memberId: string) => void;
}

/**
 * Searchable clinician picker: the account owner (self) pinned above the Team
 * group when present, showing their name + avatar rather than "Me".
 *
 * Extracted from `ClinicianSelectorPopover` so the toolbar CTA's "New session
 * for" submenu can reuse the exact same list inside a
 * `DropdownMenuSubContent` instead of a `PopoverContent`.
 */
export function ClinicianSelectorCommand({
  clinicians,
  currentUserId,
  isLoading,
  onSelect,
}: ClinicianSelectorCommandProps) {
  const intl = useIntl();

  // The account owner appears in `clinicians` only when they have
  // SESSION_CREATE; the caller's list-building hook drops self otherwise, so
  // `self` absent means no pinned row. Pinned separately at the top, so
  // exclude self from the Team group to avoid a duplicate row.
  const self = clinicians.find((user) => user.kinde_user_id === currentUserId);
  const teamClinicians = clinicians.filter(
    (user) => user.kinde_user_id !== currentUserId
  );

  return (
    <Command className="rounded-md">
      <CommandInput
        placeholder={intl.formatMessage({
          id: '/WtDsPz/1U',
          defaultMessage: 'Search user',
        })}
      />
      <CommandList>
        <CommandEmpty>
          {isLoading
            ? intl.formatMessage({
                id: 'gjBiyjshwX',
                defaultMessage: 'Loading...',
              })
            : intl.formatMessage({
                id: 'MD5B2N/txh',
                defaultMessage: 'No user found',
              })}
        </CommandEmpty>
        {self && (
          <CommandGroup>
            <ClinicianCommandItem
              member={self}
              value={`self-${self.name}`}
              testId="clinician-selector-self"
              onSelect={onSelect}
            />
          </CommandGroup>
        )}
        {teamClinicians.length > 0 && (
          <CommandGroup
            heading={intl.formatMessage({
              id: 'wsUmh9XWBm',
              defaultMessage: 'Team',
            })}
          >
            {teamClinicians.map((user, index) => (
              <ClinicianCommandItem
                key={user.kinde_user_id}
                member={user}
                value={`${user.name}-${index}`}
                testId="clinician-selector-item"
                onSelect={onSelect}
              />
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

interface ClinicianCommandItemProps {
  member: ClinicianSelectorMember;
  value: string;
  testId: string;
  onSelect: (memberId: string) => void;
}

function ClinicianCommandItem({
  member,
  value,
  testId,
  onSelect,
}: ClinicianCommandItemProps) {
  return (
    <CommandItem
      value={value}
      onSelect={() => onSelect(member.kinde_user_id)}
      data-testid={testId}
    >
      <Avatar
        size="sm"
        color={getAvatarColor(member.name)}
        className="me-2 shrink-0"
        aria-hidden="true"
      >
        <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
      </Avatar>
      {member.name}
    </CommandItem>
  );
}
